import { Scale, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '@/components/Footer';

const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <Link
            to="/"
            className="text-2xl font-bold text-capledger-teal hover:opacity-80 transition-opacity"
          >
            CapLedger
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="space-y-8">
          {/* Title Section */}
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-foreground">Terms of Service</h1>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div>
                <strong>Effective Date:</strong> October 2025
              </div>
              <div>
                <strong>Owner:</strong> Spesvest (Pty) Ltd (Registration No. 2016/197925/07)
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <strong>Address:</strong> Unit C, Doornbosch Centre, Strand Rd, Stellenbosch, South
              Africa, 7600
            </div>
            <div className="text-sm text-muted-foreground">
              <strong>Product:</strong> CapLedger (
              <a href="https://www.capledger.co.za" className="text-capledger-teal hover:underline">
                www.capledger.co.za
              </a>
              )
            </div>
          </div>

          {/* Agreement Notice */}
          <div className="bg-muted/50 border-l-4 border-capledger-teal p-4 rounded">
            <p className="text-muted-foreground leading-relaxed">
              By creating an account, accessing, or using CapLedger ("Service"), you agree to these
              Terms of Service ("Terms"). If you do not agree, do not use the Service.
            </p>
          </div>

          {/* Policy Content */}
          <div className="prose prose-slate max-w-none space-y-8">
            {/* Section 1 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">1. About the Service</h2>
              <p className="text-muted-foreground leading-relaxed">
                CapLedger is a digital equity and corporate register management platform that helps
                companies record, manage, and maintain cap tables, shareholder registers, board
                actions, valuations, and related documents. CapLedger does not provide legal,
                financial, tax, or investment advice.
              </p>
            </section>

            {/* Section 2 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">2. Eligibility & Accounts</h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>You must be 18+ and legally able to contract.</li>
                <li>
                  If you use the Service on behalf of an entity, you warrant that you have authority
                  to bind that entity.
                </li>
                <li>
                  You are responsible for safeguarding access methods (including magic links, MFA,
                  API keys). Notify us immediately of suspected unauthorised access.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">3. Acceptable Use</h2>
              <p className="text-muted-foreground leading-relaxed">You agree not to:</p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>(a) breach any law</li>
                <li>(b) upload malware or attempt to bypass security</li>
                <li>(c) scrape or rate-limit evade</li>
                <li>(d) misrepresent identity or authority</li>
                <li>(e) interfere with other users' data or the Service</li>
                <li>(f) reverse engineer or resell without permission</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                We may monitor usage to maintain security and service integrity.
              </p>
            </section>

            {/* Section 4 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">4. Your Data & Ownership</h2>
              <p className="text-muted-foreground leading-relaxed">
                You own your content and company records.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                You grant Spesvest a non-exclusive licence to process, store, back up, and transmit
                your data solely to provide and improve the Service.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                You are responsible for accuracy, lawfulness, and permissions related to any data
                you submit (including personal information of shareholders or directors).
              </p>
            </section>

            {/* Section 5 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                5. Privacy & Data Protection
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We process personal information in accordance with applicable laws, including POPIA
                and GDPR (and, where applicable, UK DPA 2018). See our{' '}
                <Link to="/privacy" className="text-capledger-teal hover:underline">
                  Privacy Policy
                </Link>{' '}
                and{' '}
                <Link to="/cookies" className="text-capledger-teal hover:underline">
                  Cookie Policy
                </Link>{' '}
                for details on categories of data, purposes, retention, transfers, and your rights.
              </p>
            </section>

            {/* Section 6 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">6. Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We implement industry-standard security controls (encryption in transit/at rest,
                logical isolation, access controls, monitoring). You are responsible for
                organisational practices on your side (role assignments, principle of least
                privilege, device hygiene).
              </p>
            </section>

            {/* Section 7 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                7. Subprocessors & Data Location
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We may use vetted service providers to operate the Service (e.g., infrastructure,
                storage, email). Where personal data is transferred across borders, appropriate
                safeguards are applied (e.g., Standard Contractual Clauses). A list of core
                subprocessors is available on request.
              </p>
            </section>

            {/* Section 8 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                8. Availability, Maintenance & Beta
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                The Service is provided on an "as-is/available" basis. We aim for high availability
                but do not guarantee uninterrupted service. We may suspend access for maintenance,
                security, or legal reasons. Features labelled "Beta" are experimental, may change,
                and are provided without warranties.
              </p>
            </section>

            {/* Section 9 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                9. Subscriptions, Billing & Taxes
              </h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  Pricing and plan limits are published on the site and may change with notice.
                </li>
                <li>Subscriptions renew automatically unless cancelled before renewal.</li>
                <li>
                  Fees are exclusive of applicable taxes; you are responsible for any VAT/sales/use
                  taxes.
                </li>
                <li>Late or failed payments may result in suspension until resolved.</li>
              </ul>
            </section>

            {/* Section 10 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">10. Confidentiality</h2>
              <p className="text-muted-foreground leading-relaxed">
                Each party may receive non-public information from the other. The receiving party
                will use such information only to perform under these Terms and will protect it
                using reasonable safeguards. Exclusions apply to information already public,
                independently developed, or lawfully obtained.
              </p>
            </section>

            {/* Section 11 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                11. Compliance & Audit Trail
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                The Service provides audit-trail capabilities to record user actions within a
                company context. You remain responsible for ensuring your use meets your legal,
                regulatory, and record-retention obligations (e.g., Companies Act requirements,
                corporate governance standards).
              </p>
            </section>

            {/* Section 12 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                12. Third-Party Integrations
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                If you connect third-party services, your use of those services is governed by their
                terms. We are not responsible for third-party services and do not warrant their
                performance.
              </p>
            </section>

            {/* Section 13 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">13. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                All software, design, know-how, and trademarks of the Service are owned by or
                licensed to Spesvest. No rights are granted except as expressly stated. You may not
                copy, modify, or create derivative works except as permitted by law.
              </p>
            </section>

            {/* Section 14 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                14. Warranties & Disclaimers
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                To the maximum extent permitted by law, the Service is provided "as is" and "as
                available". We disclaim implied warranties of merchantability, fitness for a
                particular purpose, and non-infringement. We do not warrant that the Service will be
                error-free or meet your requirements.
              </p>
            </section>

            {/* Section 15 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                15. Limitation of Liability
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                To the maximum extent permitted by law:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  Neither party is liable for indirect, incidental, special, consequential, or
                  punitive damages.
                </li>
                <li>
                  Spesvest's total aggregate liability for all claims in any 12-month period is
                  limited to the fees paid by you for the Service in that period.
                </li>
              </ul>
            </section>

            {/* Section 16 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">16. Indemnity</h2>
              <p className="text-muted-foreground leading-relaxed">
                You will indemnify and hold Spesvest harmless from claims, damages, liabilities,
                costs, and expenses arising from (a) your unlawful use of the Service; (b) your data
                or instructions; or (c) breach of these Terms.
              </p>
            </section>

            {/* Section 17 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                17. Suspension & Termination
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                You may cancel at any time via the account interface or by contacting support. We
                may suspend or terminate access with notice for material breach, security risk,
                non-payment, or legal requirements. Upon termination, you may request export of your
                data within a reasonable period.
              </p>
            </section>

            {/* Section 18 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                18. Data Export & Portability
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                You can export company records and registers using provided tools or by requesting
                an export. We may charge a reasonable fee for extensive, repeated, or bespoke export
                requests.
              </p>
            </section>

            {/* Section 19 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                19. Changes to the Service or Terms
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We may modify the Service and these Terms to improve functionality, enhance
                security, or reflect legal changes. Updates will be posted on the site with a
                revised effective date. Continued use after changes constitutes acceptance.
              </p>
            </section>

            {/* Section 20 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                20. Governing Law & Disputes
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms are governed by the laws of the Republic of South Africa. Venue for
                disputes is the competent courts of South Africa. If you are domiciled in another
                jurisdiction, mandatory consumer/data protection rights in your country may also
                apply.
              </p>
            </section>

            {/* Section 21 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">21. Notices</h2>
              <p className="text-muted-foreground leading-relaxed">
                Legal notices to Spesvest must be sent to the address below and by email. Notices to
                you may be sent to your account email.
              </p>
              <div className="bg-muted p-6 rounded-lg space-y-2">
                <p className="font-semibold text-foreground">Spesvest (Pty) Ltd — CapLedger</p>
                <p className="text-muted-foreground">
                  Unit C, Doornbosch Centre, Strand Rd, Stellenbosch, South Africa, 7600
                </p>
                <a
                  href="mailto:support@capledger.co.za"
                  className="flex items-center gap-2 text-capledger-teal hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  support@capledger.co.za
                </a>
              </div>
            </section>

            {/* Section 22 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                22. Entire Agreement & Order of Precedence
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms, together with any order forms, plan descriptions, and policies
                referenced herein (Privacy, Cookie), constitute the entire agreement and supersede
                prior communications. If there is a conflict, the order form (if any) prevails, then
                these Terms, then referenced policies.
              </p>
            </section>

            {/* Section 23 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">23. Assignment</h2>
              <p className="text-muted-foreground leading-relaxed">
                You may not assign or transfer these Terms without our prior written consent. We may
                assign to an affiliate or in connection with a merger, acquisition, or sale of
                assets.
              </p>
            </section>

            {/* Section 24 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">24. Severability & Waiver</h2>
              <p className="text-muted-foreground leading-relaxed">
                If any provision is found unenforceable, the remainder remains in effect. Failure to
                enforce a provision is not a waiver.
              </p>
            </section>

            {/* Contact Section */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                For questions about these Terms:{' '}
                <a
                  href="mailto:support@capledger.co.za"
                  className="text-capledger-teal hover:underline"
                >
                  support@capledger.co.za
                </a>
              </p>
            </section>
          </div>

          {/* Trust Badge */}
          <div className="flex items-center justify-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
            <Scale className="h-6 w-6 text-capledger-teal" />
            <span className="text-sm text-muted-foreground">
              Governed by South African law · Fair and transparent terms
            </span>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;
